import React from 'react'
import { useHistory } from 'react-router-dom'
import { Box, SimpleGrid, Loader, Group } from '@mantine/core'
import { FiHeart, FiMessageSquare, FiShare2 } from 'react-icons/fi'
import { SlopVideoCard, SlopLibraryGridSkeleton } from '@gunslop/ui'
import { EmptyState } from '../../app/common/ui'
import { toAbsoluteMediaUrl } from '../../app/api/resolveApiBaseUrl'
import AppButton from '../../app/common/ui/AppButton'
import { formatSlopDate } from './slopFeedConstants'
import type { SlopLibraryFeed } from './hooks/useSlopLibraryFeed'

type Props = Pick<
  SlopLibraryFeed,
  | 'feedPosts'
  | 'feedLoading'
  | 'hasMore'
  | 'sentinelRef'
  | 'setSelectedPost'
  | 'requireAuth'
  | 'handleToggleLike'
  | 'handleShare'
>

export default function SlopLegacyLibrary ({
  feedPosts,
  feedLoading,
  sentinelRef,
  setSelectedPost,
  requireAuth,
  handleToggleLike,
  handleShare
}: Props) {
  const history = useHistory()

  if (feedLoading && feedPosts.length === 0) {
    return <SlopLibraryGridSkeleton count={6} />
  }

  if (feedPosts.length === 0) {
    return (
      <EmptyState
        title="No videos in your library"
        message="Upload a long-form video above, or browse Discover for shoots and mixed feed items."
        action={{
          label: 'Go to Discover',
          onClick: () => history.push('/slop-feed')
        }}
      />
    )
  }

  return (
    <>
      <SimpleGrid
        cols={{ base: 1, sm: 2, md: 3 }}
        spacing="md"
        sx={{ marginBottom: 40 }}
      >
        {feedPosts.map((post) => (
          <SlopVideoCard
            key={post.id}
            post={post}
            thumbnailUrl={post.thumbnailUrl ? toAbsoluteMediaUrl(post.thumbnailUrl) : null}
            formatDate={formatSlopDate}
            onOpen={() => setSelectedPost(post)}
            actionBar={(
              <Group spacing="xs" onClick={(e) => e.stopPropagation()}>
                <AppButton
                  variant="subtle"
                  size="xs"
                  onClick={() => requireAuth(() => handleToggleLike(post.id))}
                  leftIcon={<FiHeart size={14} fill={post.isLikedByCurrentUser ? 'currentColor' : 'none'} />}
                  sx={{ color: post.isLikedByCurrentUser ? 'var(--gs-accent)' : 'var(--gs-muted)' }}
                >
                  {post.likeCount}
                </AppButton>
                <AppButton
                  variant="subtle"
                  size="xs"
                  onClick={() => setSelectedPost(post)}
                  leftIcon={<FiMessageSquare size={14} />}
                  sx={{ color: 'var(--gs-muted)' }}
                >
                  {post.commentCount}
                </AppButton>
                <AppButton
                  variant="subtle"
                  size="xs"
                  onClick={() => handleShare(post)}
                  leftIcon={<FiShare2 size={14} />}
                  sx={{ color: 'var(--gs-muted)' }}
                />
              </Group>
            )}
          />
        ))}
      </SimpleGrid>
      <div ref={sentinelRef} style={{ height: 20 }} />
      {feedLoading && <Loader style={{ margin: '0 auto' }} />}
    </>
  )
}
